/**
 * PropertyOverview — the property workspace home.
 *
 * What lands here when the user picks a property: a single dashboard
 * tile per data layer with a "tap to drill in" affordance + a
 * headline number when one's available. Each tile is also a link to
 * the corresponding /p/:id/<layer> page.
 *
 * Today: tiles are placeholders that link out. As the data layers
 * fill in (Ahrefs first, then GSC/GA4/etc.), each tile gains its
 * actual headline number sourced from the substrate.
 */
import { useParams, Link } from "react-router-dom";
import {
  Search, BookOpen, BarChart3, Globe, Activity,
  Server, Sparkles, ScrollText, FileText, ArrowRight,
  Link as LinkIcon, Tag, MessageSquare, Sliders, Gauge,
} from "lucide-react";
import { useProperty } from "@/lib/queries";
import { cn } from "@/lib/utils";


const TILES = [
  { tail: "crawl",       label: "Crawl",          desc: "Frog crawl history + per-URL data", icon: Search,    primary: true },
  { tail: "pages",       label: "Pages",          desc: "Cross-source per-URL view (Frog + GSC + GA4 + DFS joined)",  icon: BookOpen, primary: true },
  { tail: "ahrefs",      label: "Ahrefs",         desc: "Backlinks, refdomains, anchors, organic keywords", icon: BarChart3, primary: true },
  { tail: "dataforseo",  label: "DataForSEO",     desc: "Cross-vendor backlinks + keywords + SERP + Lighthouse",  icon: BarChart3, primary: true },
  { tail: "gsc",         label: "Search Console", desc: "Google's view of search performance — queries, pages, sitemaps", icon: Globe,    primary: true },
  { tail: "ga4",         label: "Analytics",      desc: "Real visitor sessions, channels, devices, landing-page bounce", icon: Activity, primary: true },
  { tail: "backlinks",   label: "Backlinks",      desc: "Cross-source ref-domains (Ahrefs ∪ DFS) with vendor-disagreement flags", icon: LinkIcon, primary: true },
  { tail: "keywords",    label: "Keywords",       desc: "Cross-source keyword universe (GSC + Ahrefs + DFS unioned with disagreement flags)",   icon: Tag,    primary: true },
  { tail: "server-logs", label: "Server logs",    desc: "First-party access log — bots, 404s, 5xx, real visitor URLs", icon: Server, primary: true },
  { tail: "reviews",     label: "Reviews",        desc: "Google + Trustpilot review monitoring (DFS-scraped — workaround for blocked GBP)", icon: MessageSquare, primary: true },
  { tail: "cwv",         label: "Core Web Vitals", desc: "Real-user CrUX field data + Lighthouse lab — LCP/INP/CLS by URL, mobile vs desktop", icon: Gauge, primary: true },
  { tail: "insights",    label: "Insights",       desc: "Cross-source findings — Auriel's wow layer ranked by impact",     icon: Sparkles, primary: true },
  { tail: "audits",      label: "Audits",         desc: "Audit history for this property",            icon: ScrollText },
  { tail: "reports",     label: "Reports",        desc: "Customer-facing reports for this property",  icon: FileText },
  { tail: "settings",    label: "Settings",       desc: "Per-property config — targeting, mode, Brand Radar, cadence", icon: Sliders },
] as const;


export default function PropertyOverview() {
  const { propertyId: rawId } = useParams<{ propertyId: string }>();
  const propertyId = Number(rawId);
  const property = useProperty(propertyId);

  if (property.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }
  if (!property.data) {
    return <div className="text-sm text-red-600">Property not found.</div>;
  }
  const p = property.data;

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">Property</div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          <span className="font-mono">{p.domain}</span>
        </h1>
        <p className="text-sm text-muted-foreground">{p.display_name}</p>
      </div>

      {/* Quick stats from properties row itself */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Target keywords"   value={p.target_keywords.length} />
        <Stat label="Competitors"        value={p.competitor_domains.length} />
        <Stat label="Brand terms"        value={p.brand_terms.length} />
        <Stat label="Audit cron"         value={p.audit_cron} mono />
      </div>

      {/* Data-layer tiles */}
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Data layers
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {TILES.map((t) => <Tile key={t.tail} propertyId={propertyId} tile={t} />)}
        </div>
      </div>
    </div>
  );
}


function Tile({ propertyId, tile }: {
  propertyId: number;
  tile: typeof TILES[number];
}) {
  const Icon = tile.icon;
  const isPlaceholder = (tile as { placeholder?: boolean }).placeholder;
  const isPrimary = (tile as { primary?: boolean }).primary;
  return (
    <Link
      to={`/p/${propertyId}/${tile.tail}`}
      className={cn(
        "group block rounded-md border bg-card p-4 transition-colors hover:border-primary/50",
        isPrimary ? "border-primary/30" : "border-border",
      )}
    >
      <div className="flex items-start gap-3">
        <div className={cn(
          "rounded-md p-2",
          isPrimary ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
        )}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-medium">{tile.label}</h3>
            {isPlaceholder && (
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                soon
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{tile.desc}</p>
        </div>
        <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
    </Link>
  );
}


function Stat({ label, value, mono }: { label: string; value: string | number; mono?: boolean }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn("mt-0.5 text-lg font-semibold", mono && "font-mono")}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
